/**
 * A status, in a table cell, with its icon and its words.
 *
 * `DESIGN.md` §4 rule 4: "The same applies to attendance status — on-time / late /
 * early / missing / leave / incomplete are icon + text, not six colours." Nine
 * states are `StatusNote`; a *row-level* status is this. `states/index.ts` already
 * anticipated it: `StatusNote` and `Icon` are exported "because a later screen will
 * need a severity badge that is not one of the nine … and the alternative is that it
 * invents its own and loses the icon+text guarantee".
 *
 * So the guarantee is structural here too. There is no prop that renders the colour
 * without the icon and the label: `label` is required, `icon` is required, and the
 * severity only chooses which token the pair is painted in. A badge cannot be
 * colour-only by accident, which is the same argument `StatusNote` makes one size
 * up.
 *
 * `title` is the tooltip spec 01 §19 asks for on jargon. It is **additional** to the
 * label, never a replacement: a tooltip is unreachable by touch and is not read by
 * every screen reader, so nothing a person must know may live only there.
 */

import type { ReactNode } from 'react';

import { useT } from '../i18n';
import type { MessageKey } from '../i18n/messages';
import { Icon, type IconName, type Severity } from './icons';

const SEVERITY_LABEL_KEY: Readonly<Record<Severity, MessageKey>> = {
  critical: 'severity.critical',
  high: 'severity.high',
  medium: 'severity.medium',
  low: 'severity.low',
  info: 'severity.info',
};

/**
 * How loudly the badge is painted. **Not a sixth severity** — `DESIGN.md` §4's table
 * has five rows and keeps five, and there is no `--wp-color-sev-neutral` token.
 *
 * `neutral` is for a status that is the ordinary case: a person at work, a device
 * that is simply offline, a day with no shift configured. Luật Màu Có Nghĩa asks
 * what truth a colour is telling, and "nothing has happened" is not one — a column
 * where every row is painted is a column where the row that needs a person no longer
 * stands out. The glyph and the word stay, so two of Luật Ba Kênh's three channels
 * are untouched; only the third is given up, and `data-severity` still carries the
 * underlying classification for anything downstream that reads it.
 */
export type BadgeTone = 'severity' | 'neutral';

export interface BadgeProps {
  readonly severity: Severity;
  readonly icon: IconName;
  /** The words. Mandatory — colour and icon are the redundant channels, not this. */
  readonly label: string;
  /** Tooltip for a term the person may not know. Never the only place a fact lives. */
  readonly title?: string;
  /** `'severity'` (the default) paints the severity; `'neutral'` spends no colour. */
  readonly tone?: BadgeTone;
  readonly testId?: string;
}

export function Badge({
  severity,
  icon,
  label,
  title,
  tone = 'severity',
  testId,
}: BadgeProps): ReactNode {
  const t = useT();
  const neutral = tone === 'neutral';
  return (
    <span
      className={neutral ? 'wp-badge wp-badge--neutral' : `wp-badge wp-badge--${severity}`}
      data-severity={severity}
      data-tone={tone}
      {...(title === undefined ? {} : { title })}
      {...(testId === undefined ? {} : { 'data-testid': testId })}
    >
      <Icon name={icon} className="wp-badge__icon" />
      {/* The spoken half of the colour channel. A neutral badge paints no severity, so
          announcing one would describe paint that is not on the screen — the label
          beside it ("Đang làm việc", "Chưa cấu hình ca") already says the whole fact. */}
      {neutral ? null : (
        <span className="wp-visually-hidden">{t(SEVERITY_LABEL_KEY[severity])}. </span>
      )}
      <span>{label}</span>
    </span>
  );
}
